import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { authenticationService } from "@app/login/auth.service";
import { ToastrService } from "ngx-toastr";
import { SelectItem } from "primeng/api";
import { Permissions } from "@models/permissions.enum";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BoundedTableComponent } from "../../bounded-table/bounded-table.component";
import { LongevityViewModel } from "@app/shared/models/rates/LongevityViewModel.model";
import { Rate } from "@app/shared/models/rates/Rate.model";

@Component({
  selector: "app-edit-longevity",
  templateUrl: "./edit-longevity.component.html",
  styleUrls: ["./edit-longevity.component.css"],
})
export class EditLongevityComponent implements OnInit {
  @ViewChild("seniorityTable") seniorityTable: BoundedTableComponent;

  @Input() rate: LongevityViewModel;
  @Input() rateId: number;
  @Input() rateType: string;
  @Input() isEditing: boolean;
  @Input() isCreating: boolean;

  rates: Rate[] = [];
  columns: any[];

  chargePerOptions: SelectItem[];

  drivers = [];
  selectedDrivers = [];
  isDefaultDriver: boolean;

  constructor(
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private authService: authenticationService
  ) {
    this.chargePerOptions = [
      { label: "Trip", value: "TRIP" },
      { label: "Mile", value: "MILE" },
    ];
    this.columns = [
      {
        field: "price",
        name: "BONUS/MILE",
        type: "dollar",
      },
    ];
  }

  ngOnInit(): void {
    if (!this.isCreating) {
      this.loadRates(this.rate);
    } else {
      this.rate.chargePer = "TRIP";
    }

    this.drivers = this.route.snapshot.data["drivers"].filter((driver) => driver.isActive);

    if (!this.isCreating) {
      this.selectedDrivers = this.getDropdownValue(this.drivers, this.rate.drivers, "id", "id");
    }
  }

  hasPermission(permissionName: string) {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  getDropdownValue(options: any[], items: any[], optionsLabel: string, itemLabel: string) {
    return items.map((item) => {
      return options.find((option) => option[optionsLabel] == item[itemLabel]);
    });
  }

  validate = () => {
    const warnings = [];
    if (!this.rate.name) warnings.push("Rate Name is required");
    if (!this.rate.chargeTypeId) warnings.push("Charge type is required");
    if (this.rate.chargePer === "MILE") {
      if (!this.seniorityTable.validateRanges()) {
        warnings.push("Seniority table is not valid. Day Ranges must be 1 apart between rows");
      }
    }

    if (warnings.length > 0) {
      for (const warning of warnings) {
        this.toastr.warning(warning);
      }
      return false;
    }
    return true;
  };

  mapRatesToTable(rates: Rate[]) {
    let rows = [];
    let bounds = {};

    // create the row objects
    for (let item of rates) {
      const { floor, roof, kind, price } = item;
      const boundsKey = `${floor}-${roof}`;

      if (!bounds[boundsKey]) {
        bounds[boundsKey] = { floor, roof, values: {} };
      }

      bounds[boundsKey].values["price"] = price;
    }

    rows = Object.values(bounds);
    rows = rows.sort((row1, row2) => row1.floor - row2.floor);

    return rows;
  }

  loadRates(longevityRate: LongevityViewModel) {
    this.isDefaultDriver = longevityRate.isDefaultDriver;
    this.rates = this.mapRatesToTable(longevityRate.longevityRates);
  }

  /*
    For parsing this page's data into a request object
  */
  buildRatesFromRows = (rows: any[]) => {
    // map from the rates table to individual rates items
    let rates = [];
    for (const row of rows) {
      const { floor, roof, values } = row;
      for (const [name, price] of Object.entries(values)) {
        const rate = { floor, roof, price };
        rates.push(rate);
      }
    }
    return rates;
  };

  // Build the object to send to the server
  buildRatesObject = () => {
    const rateObject = {
      ...this.rate,

      longevityRates: this.buildRatesFromRows(this.rates),
      isDefaultDriver: this.isDefaultDriver,

      drivers: this.selectedDrivers
        .filter((driver) => driver?.id)
        .map((driver) => ({ id: driver.id })),
    };

    return rateObject;
  };

  exportExcel(rows: any[], cols: any[], title: string) {
    if (rows === undefined || cols === undefined) {
      this.toastr.warning("Table is empty.", "Unable to Export", {
        closeButton: true,
        enableHtml: true,
      });
      return;
    }

    //Get readable column headers
    const exportRows = [];
    rows.forEach((row) => {
      let thisExportRow = {} as any;
      if (row.floor != undefined && row.roof != undefined) {
        thisExportRow.DAYS = row.floor + "-" + row.roof;
      }

      if (row.values != undefined) {
        cols.forEach((col) => {
          thisExportRow[col.name.toUpperCase()] = row.values[col.field];
        });
      } else {
        thisExportRow = row;
      }

      exportRows.push(thisExportRow);
    });

    if (exportRows.length > 0) {
      import("xlsx").then((xlsx) => {
        const worksheet = xlsx.utils.json_to_sheet(exportRows);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
        const excelBuffer: any = xlsx.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        this.saveAsExcelFile(excelBuffer, title);
      });
    } else {
      this.toastr.warning("Table is empty.", "Unable to Export", {
        closeButton: true,
        enableHtml: true,
      });
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
  }

  importExcel(event: any, buildFunction, cols: any[], table: any, fileUpload: any) {
    let reader = new FileReader();
    reader.readAsArrayBuffer(event.files[0]);
    reader.onload = (e) => {
      let data = new Uint8Array(reader.result as ArrayBuffer);
      let workbook = XLSX.read(data, { type: "array" });
      let sheet = workbook.Sheets[workbook.SheetNames[0]];
      let result = XLSX.utils.sheet_to_json(sheet);

      let exit = false;

      //Check that columns match
      cols.forEach((col) => {
        if (exit) {
          return;
        }
        result.forEach((resultRow: any[]) => {
          if (exit) {
            return;
          }
          if (resultRow[col.name] === undefined) {
            this.toastr.warning("Columns do not match this table.", "Unable to Import", {
              closeButton: true,
              enableHtml: true,
            });
            exit = true;
            return;
          }
        });
      });
      if (exit) {
        fileUpload.clear();
        return;
      }

      //Add to table
      try {
        let formattedResult = buildFunction(result);
        table.rows = this.mapRatesToTable(formattedResult);
        table.updatedRows();

        this.toastr.success("Columns successfully imported.", "Success", {
          closeButton: true,
          enableHtml: true,
        });
      } catch (error) {
        this.toastr.warning("Columns do not match this table.", "Unable to Import", {
          closeButton: true,
          enableHtml: true,
        });
      }

      fileUpload.clear();
    };
  }

  loadBoundedExcelData(data: any[]) {
    let rates = [];
    for (const row of data) {
      const days = row["DAYS"].split("-");
      const [floor, roof] = days;
      delete row["DAYS"];

      for (const [kind, price] of Object.entries(row)) {
        const rate = { floor, roof, kind, price };
        rates.push(rate);
      }
    }

    return rates;
  }
}
